import React, { useState, useEffect } from "react"
import {
  Card,
  CardBody,
  CardHeader,
  Row,
  Col,
  Alert,
  Badge,
  Progress,
  Button,
  Input,
  Label,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap"

// Documentos requeridos según tipo de sociedad
const DOCUMENTOS_BASE = [
  {
    id: "estatuto",
    nombre: "Estatuto Social / Contrato Constitutivo",
    descripcion: "Copia certificada ante escribano público",
    requerido: false,
    categoria: "legal",
  },
  {
    id: "poderes",
    nombre: "Poderes Vigentes",
    descripcion: "Del representante legal autorizado para operar",
    requerido: false,
    categoria: "legal",
  },
  {
    id: "acta_directorio",
    nombre: "Acta de Directorio / Asamblea",
    descripcion: "Autorizando la solicitud de crédito",
    requerido: false,
    categoria: "legal",
  },
  {
    id: "dni_representantes",
    nombre: "DNI de Representantes Legales",
    descripcion: "Frente y dorso de todos los firmantes",
    requerido: false,
    categoria: "identidad",
  },
  {
    id: "constancia_afip",
    nombre: "Constancia de Inscripción AFIP",
    descripcion: "Actualizada (no mayor a 30 días)",
    requerido: false,
    categoria: "fiscal",
  },
  {
    id: "eeff_ultimo",
    nombre: "Estados Financieros - Último Ejercicio",
    descripcion: "Firmados por contador público y certificados",
    requerido: true,
    categoria: "financiero",
  },
  {
    id: "eeff_anterior",
    nombre: "Estados Financieros - Ejercicio Anterior",
    descripcion: "Firmados por contador público y certificados",
    requerido: false,
    categoria: "financiero",
  },
  {
    id: "ddjj_iva",
    nombre: "DDJJ IVA - Últimos 6 meses",
    descripcion: "Formularios F731 presentados",
    requerido: false,
    categoria: "fiscal",
  },
  {
    id: "ddjj_ganancias",
    nombre: "DDJJ Ganancias - Último Ejercicio",
    descripcion: "Formulario F713 presentado",
    requerido: false,
    categoria: "fiscal",
  },
  {
    id: "certificado_pyme",
    nombre: "Certificado PyME (si aplica)",
    descripcion: "Emitido por AFIP vigente",
    requerido: false,
    categoria: "fiscal",
  },
  {
    id: "referencias_bancarias",
    nombre: "Referencias Bancarias",
    descripcion: "De las entidades donde opera",
    requerido: false,
    categoria: "financiero",
  },
  {
    id: "referencias_comerciales",
    nombre: "Referencias Comerciales",
    descripcion: "De principales proveedores o clientes",
    requerido: false,
    categoria: "comercial",
  },
]

const CATEGORIAS = {
  legal: { nombre: "Documentos Legales", color: "primary", icon: "bx-file" },
  identidad: { nombre: "Identidad", color: "info", icon: "bx-id-card" },
  fiscal: {
    nombre: "Documentación Fiscal",
    color: "warning",
    icon: "bx-receipt",
  },
  financiero: {
    nombre: "Información Financiera",
    color: "success",
    icon: "bx-line-chart",
  },
  comercial: { nombre: "Referencias", color: "secondary", icon: "bx-building" },
}

const ESTADOS_DOC = {
  pendiente: { label: "Pendiente", color: "secondary", icon: "bx-time" },
  cargado: { label: "Cargado", color: "info", icon: "bx-upload" },
  en_revision: { label: "En Revisión", color: "warning", icon: "bx-search" },
  aprobado: { label: "Aprobado", color: "success", icon: "bx-check" },
  rechazado: { label: "Rechazado", color: "danger", icon: "bx-x" },
}

// Componente de item de documento
const DocumentoItem = ({
  doc,
  estado,
  onUpload,
  onChangeEstado,
  onVerDetalle,
}) => {
  const estadoConfig = ESTADOS_DOC[estado?.estado || "pendiente"]
  const tieneArchivo = estado?.archivo

  return (
    <div
      className={`d-flex align-items-center p-3 border-bottom ${
        estado?.estado === "rechazado" ? "bg-danger bg-opacity-10" : ""
      }`}
    >
      {/* Icono de estado */}
      <div className="me-3">
        <i
          className={`bx ${estadoConfig.icon} text-${estadoConfig.color}`}
          style={{ fontSize: 24 }}
        />
      </div>

      {/* Info del documento */}
      <div className="flex-grow-1">
        <div className="d-flex align-items-center">
          <strong>{doc.nombre}</strong>
          {doc.requerido && (
            <Badge color="danger" className="ms-2" style={{ fontSize: 9 }}>
              REQUERIDO
            </Badge>
          )}
        </div>
        <small className="text-muted">{doc.descripcion}</small>
        {estado?.observacion && (
          <div className="mt-1">
            <small className="text-danger">
              <i className="bx bx-info-circle me-1" />
              {estado.observacion}
            </small>
          </div>
        )}
        {tieneArchivo && (
          <div className="mt-1">
            <small className="text-muted">
              <i className="bx bx-file me-1" />
              {estado.archivo.nombre} - {estado.archivo.fecha}
            </small>
          </div>
        )}
      </div>

      {/* Acciones */}
      <div className="d-flex gap-2 align-items-center">
        <Badge color={estadoConfig.color}>{estadoConfig.label}</Badge>

        {!tieneArchivo ? (
          <Button color="primary" size="sm" onClick={() => onUpload(doc.id)}>
            <i className="bx bx-upload me-1" />
            Cargar
          </Button>
        ) : (
          <>
            <Button
              color="light"
              size="sm"
              onClick={() => onVerDetalle(doc.id)}
              title="Ver documento"
            >
              <i className="bx bx-show" />
            </Button>
            <Button
              color="light"
              size="sm"
              onClick={() => onUpload(doc.id)}
              title="Reemplazar"
            >
              <i className="bx bx-refresh" />
            </Button>
          </>
        )}
      </div>
    </div>
  )
}

// Componente principal
const Documentacion = ({
  tipoSociedad,
  documentosEstado,
  setDocumentosEstado,
  setDocumentacionCompleta,
}) => {
  const [modalUpload, setModalUpload] = useState({ open: false, docId: null })
  const [archivoSeleccionado, setArchivoSeleccionado] = useState(null)

  // Obtener documentos aplicables
  const documentos = DOCUMENTOS_BASE

  // Calcular progreso
  const docsRequeridos = documentos.filter(d => d.requerido)
  const docsRequeridosAprobados = docsRequeridos.filter(
    d => documentosEstado[d.id]?.estado === "aprobado",
  )
  const progreso = Math.round(
    (docsRequeridosAprobados.length / docsRequeridos.length) * 100,
  )

  // Agrupar por categoría
  const docsPorCategoria = {}
  documentos.forEach(doc => {
    if (!docsPorCategoria[doc.categoria]) {
      docsPorCategoria[doc.categoria] = []
    }
    docsPorCategoria[doc.categoria].push(doc)
  })

  // Actualizar estado de completitud
  useEffect(() => {
    const todosRequeridosOK = docsRequeridos.every(
      d =>
        documentosEstado[d.id]?.estado === "aprobado" ||
        documentosEstado[d.id]?.estado === "cargado" ||
        documentosEstado[d.id]?.estado === "en_revision",
    )
    if (setDocumentacionCompleta) {
      setDocumentacionCompleta(todosRequeridosOK)
    }
  }, [documentosEstado])

  const handleUpload = docId => {
    setModalUpload({ open: true, docId })
    setArchivoSeleccionado(null)
  }

  const handleConfirmUpload = () => {
    if (!archivoSeleccionado || !modalUpload.docId) return

    setDocumentosEstado(prev => ({
      ...prev,
      [modalUpload.docId]: {
        estado: "cargado",
        archivo: {
          nombre: archivoSeleccionado.name,
          fecha: new Date().toLocaleDateString("es-AR"),
          size: archivoSeleccionado.size,
        },
        fechaCarga: new Date().toISOString(),
      },
    }))

    setModalUpload({ open: false, docId: null })
    setArchivoSeleccionado(null)
  }

  const handleVerDetalle = docId => {
    // TODO: Abrir visor de documento
    console.log("Ver documento:", docId)
  }

  return (
    <div>
      <h5 className="mb-3">Documentación Requerida</h5>
      <p className="text-muted mb-4">
        Cargue la documentación solicitada. Los documentos marcados como
        requeridos son obligatorios para continuar con el proceso.
      </p>

      {/* Barra de progreso */}
      <Card className="mb-4">
        <CardBody>
          <div className="d-flex justify-content-between align-items-center mb-2">
            <span>Progreso de documentación</span>
            <span className="fw-bold">{progreso}%</span>
          </div>
          <Progress
            value={progreso}
            color={progreso === 100 ? "success" : "primary"}
          />
          <small className="text-muted mt-2 d-block">
            {docsRequeridosAprobados.length} de {docsRequeridos.length}{" "}
            documentos requeridos completados
          </small>
        </CardBody>
      </Card>

      {/* Documentos por categoría */}
      {Object.entries(docsPorCategoria).map(([categoria, docs]) => {
        const catConfig = CATEGORIAS[categoria]
        return (
          <Card className="mb-3" key={categoria}>
            <CardHeader className="bg-light py-2">
              <div className="d-flex align-items-center">
                <i
                  className={`bx ${catConfig.icon} me-2 text-${catConfig.color}`}
                />
                <strong>{catConfig.nombre}</strong>
                <Badge color="light" className="ms-auto">
                  {
                    docs.filter(
                      d => documentosEstado[d.id]?.estado === "aprobado",
                    ).length
                  }
                  /{docs.length}
                </Badge>
              </div>
            </CardHeader>
            <CardBody className="p-0">
              {docs.map(doc => (
                <DocumentoItem
                  key={doc.id}
                  doc={doc}
                  estado={documentosEstado[doc.id]}
                  onUpload={handleUpload}
                  onVerDetalle={handleVerDetalle}
                />
              ))}
            </CardBody>
          </Card>
        )
      })}

      {/* Alerta si faltan documentos */}
      {progreso < 100 && (
        <Alert color="warning">
          <i className="bx bx-info-circle me-2" />
          Faltan {docsRequeridos.length - docsRequeridosAprobados.length}{" "}
          documentos requeridos por cargar o aprobar.
        </Alert>
      )}

      {/* Modal de carga */}
      <Modal
        isOpen={modalUpload.open}
        toggle={() => setModalUpload({ open: false, docId: null })}
      >
        <ModalHeader
          toggle={() => setModalUpload({ open: false, docId: null })}
        >
          Cargar Documento
        </ModalHeader>
        <ModalBody>
          {modalUpload.docId && (
            <>
              <p className="mb-3">
                <strong>
                  {documentos.find(d => d.id === modalUpload.docId)?.nombre}
                </strong>
              </p>
              <div className="mb-3">
                <Label>Seleccionar archivo</Label>
                <Input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                  onChange={e => setArchivoSeleccionado(e.target.files[0])}
                />
                <small className="text-muted">
                  Formatos aceptados: PDF, JPG, PNG, DOC, DOCX. Máximo 10MB.
                </small>
              </div>
              {archivoSeleccionado && (
                <Alert color="info" className="mb-0">
                  <i className="bx bx-file me-2" />
                  {archivoSeleccionado.name} (
                  {(archivoSeleccionado.size / 1024 / 1024).toFixed(2)} MB)
                </Alert>
              )}
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            onClick={() => setModalUpload({ open: false, docId: null })}
          >
            Cancelar
          </Button>
          <Button
            color="primary"
            onClick={handleConfirmUpload}
            disabled={!archivoSeleccionado}
          >
            <i className="bx bx-upload me-1" />
            Cargar Documento
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  )
}

export default Documentacion
